import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, CardHeader, CardContent, CardTitle } from '../../shadcn/components/ui/card';
import { Button } from '../../shadcn/components/ui/button';
import { AuthorizedMeeting, EvaluationDailyStats, FlaggedEvaluation } from 'api-types';
import {
  ArrowLeft,
  Phone,
  User,
  AlertTriangle,
  Route,
  HelpCircle,
  ExternalLink,
  ChevronLeft,
  ChevronRight,
  Calendar,
} from 'lucide-react';
import { SpinnerLoader } from '../../components/Loader/SpinnerLoader';
import { ErrorCard } from '../../components/ErrorHandling';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../shadcn/components/ui/select';
import {
  fetchAuthorizedMeetings,
  fetchEvaluationDailyStats,
  fetchFlaggedEvaluations,
} from '../../api/api';
import MeetingHome from '../meeting/MeetingHome';

// ─────────────────────────────────────────────
// Stat Card Component
// ─────────────────────────────────────────────

interface StatCardProps {
  title: string;
  value: number;
  icon: React.ReactNode;
  variant: 'default' | 'success' | 'warning' | 'danger';
  subtitle?: string;
}

function StatCard({ title, value, icon, variant, subtitle }: StatCardProps) {
  const variantClasses = {
    default: 'bg-gray-50 border-gray-200 text-gray-700',
    success: 'bg-green-50 border-green-200 text-green-700',
    warning: 'bg-yellow-50 border-yellow-200 text-yellow-700',
    danger: 'bg-red-50 border-red-200 text-red-700',
  };

  return (
    <div className={`rounded-lg border-2 p-4 ${variantClasses[variant]}`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium opacity-80">{title}</p>
          <p className="text-3xl font-bold mt-1">{value}</p>
          {subtitle && <p className="text-xs mt-1 opacity-70">{subtitle}</p>}
        </div>
        <div className="opacity-50">{icon}</div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// Flagged Call Card Component
// ─────────────────────────────────────────────

function FlaggedCallCard({
  evaluation,
  onViewDetails,
}: {
  evaluation: FlaggedEvaluation;
  onViewDetails: () => void;
}) {
  const name = `${evaluation.firstName || ''} ${evaluation.lastName || ''}`.trim() || 'Unknown';
  const time = evaluation.createdAt
    ? new Date(evaluation.createdAt).toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
      })
    : '';

  const issues: string[] = [];

  if (evaluation.humanRequested) issues.push('Human Requested');
  if (evaluation.frustrated) issues.push('User Frustrated');
  if (evaluation.routingCorrect === false) issues.push('Routing Failed');
  if (evaluation.nameCaptured === 'agent_missed') issues.push('Name Not Captured');
  if (evaluation.phoneCaptured === 'agent_missed') issues.push('Phone Not Captured');

  return (
    <div className="border-b border-gray-200 p-4">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <span className="font-medium">{name}</span>
            <span className="text-gray-400">|</span>
            <span className="text-sm text-gray-500">{time}</span>
            {evaluation.phoneNumber && (
              <>
                <span className="text-gray-400">|</span>
                <span className="text-sm text-gray-500">{evaluation.phoneNumber}</span>
              </>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            {issues.map((label, idx) => (
              <span
                key={idx}
                className="text-xs border border-gray-300 rounded px-2 py-0.5 text-gray-600"
              >
                {label}
              </span>
            ))}
          </div>
        </div>
        <Button
          variant="ghost"
          size="sm"
          className="text-blue-600 hover:text-blue-800"
          onClick={onViewDetails}
        >
          View Call <ExternalLink size={14} className="ml-1" />
        </Button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// Main Dashboard Component
// ─────────────────────────────────────────────

const getLocalDateString = () => {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

export default function EvaluationDashboard() {
  const navigate = useNavigate();
  const { meetingId: urlMeetingId, pageParam } = useParams<{
    meetingId: string;
    pageParam?: string;
  }>();

  const page = pageParam ? parseInt(pageParam, 10) : 1;

  // Dashboard state (only used when meetingId is present)
  const [meetings, setMeetings] = useState<AuthorizedMeeting[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState<EvaluationDailyStats | null>(null);
  const [flaggedCalls, setFlaggedCalls] = useState<FlaggedEvaluation[]>([]);
  const [flaggedTotal, setFlaggedTotal] = useState<number>(0);
  const [selectedDate, setSelectedDate] = useState<string>(() => {
    if (urlMeetingId) {
      const stored = sessionStorage.getItem(`evalDashboard_selectedDate_${urlMeetingId}`);
      if (stored) return stored;
    }
    return getLocalDateString();
  });

  // ── Load meetings for dropdown ──
  useEffect(() => {
    if (urlMeetingId) {
      fetchAuthorizedMeetings()
        .then(response => setMeetings(response.meetings ?? []))
        .catch(() => {});
    }
  }, [urlMeetingId]);

  // ── Fetch daily stats & flagged calls ──
  const fetchData = useCallback(async () => {
    if (!urlMeetingId || !selectedDate) return;

    const fromDate = new Date(`${selectedDate}T00:00:00`).toISOString();
    const toDate = new Date(`${selectedDate}T23:59:59`).toISOString();

    setLoading(true);
    setError(null);
    try {
      const [statsResult, flaggedResult] = await Promise.all([
        fetchEvaluationDailyStats({
          meetingId: urlMeetingId,
          fromDate,
          toDate,
        }),
        fetchFlaggedEvaluations({
          meetingId: urlMeetingId,
          fromDate,
          toDate,
          page: 1,
          pageSize: 50,
        }),
      ]);

      setStats(statsResult.stats || null);
      setFlaggedCalls(flaggedResult.evaluations || []);
      setFlaggedTotal(flaggedResult.total || 0);
    } catch (err) {
      setError('Error loading evaluation data. Please try again later.');
      setStats(null);
      setFlaggedCalls([]);
    } finally {
      setLoading(false);
    }
  }, [urlMeetingId, selectedDate]);

  useEffect(() => {
    if (urlMeetingId) {
      fetchData();
    }
  }, [fetchData, urlMeetingId]);

  // ── Date navigation ──
  const handleDateChange = (dateValue: string) => {
    setSelectedDate(dateValue);
    if (urlMeetingId) {
      sessionStorage.setItem(`evalDashboard_selectedDate_${urlMeetingId}`, dateValue);
    }
  };

  const navigateDate = (direction: 'prev' | 'next') => {
    const current = new Date(selectedDate);
    if (direction === 'prev') {
      current.setDate(current.getDate() - 1);
    } else {
      current.setDate(current.getDate() + 1);
    }
    const newDate = `${current.getFullYear()}-${String(current.getMonth() + 1).padStart(2, '0')}-${String(current.getDate()).padStart(2, '0')}`;
    handleDateChange(newDate);
  };

  const formatDateDisplay = (dateStr: string) => {
    const date = new Date(dateStr);
    const today = new Date();
    const isToday = date.toDateString() === today.toDateString();

    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    const isYesterday = date.toDateString() === yesterday.toDateString();

    if (isToday) return 'Today';
    if (isYesterday) return 'Yesterday';

    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    });
  };

  // ── Navigation ──
  const handleMeetingChange = (newMeetingId: string) => {
    setStats(null);
    setFlaggedCalls([]);
    navigate(`/admin/evaluations/${newMeetingId}`);
  };

  const handleViewDetails = (meetingAssessmentId: string) => {
    window.open(
      `/admin/evaluation-report/${urlMeetingId}/${page}/${meetingAssessmentId}`,
      '_blank'
    );
  };

  const goBackToEvaluations = () => {
    navigate('/admin/evaluations');
  };

  // ─────────────────────────────────────────────
  // STATE 1: No meetingId → Reuse MeetingHome with evaluation paths
  // ─────────────────────────────────────────────
  if (!urlMeetingId) {
    return (
      <MeetingHome
        title="Evaluation Dashboard"
        viewPath={meetingId => `/admin/evaluations/${meetingId}`}
        showUsage={false}
      />
    );
  }

  // ─────────────────────────────────────────────
  // STATE 2: Has meetingId → Show daily dashboard
  // ─────────────────────────────────────────────
  return (
    <div>
      {/* Top Bar */}
      <div className="flex items-center mb-4 gap-2 justify-between">
        <Button
          variant="ghost"
          className="p-2 h-10 flex items-center justify-center text-gray-700 hover:bg-gray-100"
          onClick={goBackToEvaluations}
        >
          <ArrowLeft className="h-5 w-5" />
          <span className="text-base font-medium">Back</span>
        </Button>
        <div className="flex-1" />
        <div className="flex items-center gap-3">
          <Select value={urlMeetingId} onValueChange={handleMeetingChange}>
            <SelectTrigger className="w-[240px]">
              <SelectValue placeholder="Select Assistant" />
            </SelectTrigger>
            <SelectContent>
              {meetings.map(meeting => (
                <SelectItem key={meeting.id} value={meeting.id || ''}>
                  {meeting.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Daily Date Picker */}
          <div className="flex items-center bg-white border border-gray-300 rounded-lg p-1">
            <button
              onClick={() => navigateDate('prev')}
              className="p-1.5 hover:bg-gray-100 transition-colors rounded-md"
              aria-label="Previous day"
            >
              <ChevronLeft size={16} />
            </button>
            <div className="flex items-center gap-2 px-3 py-1 font-medium min-w-[140px] justify-center">
              <Calendar size={14} className="text-gray-500" />
              <span>{formatDateDisplay(selectedDate)}</span>
            </div>
            <button
              onClick={() => navigateDate('next')}
              className="p-1.5 hover:bg-gray-100 transition-colors rounded-md disabled:opacity-30 disabled:hover:bg-transparent"
              aria-label="Next day"
              disabled={selectedDate === getLocalDateString()}
            >
              <ChevronRight size={16} />
            </button>
          </div>

          {/* View All Calls Button */}
          <Button
            variant="outline"
            className="flex items-center gap-2"
            onClick={() => window.open(`/admin/evaluations/${urlMeetingId}/calls`, '_blank')}
          >
            <Phone size={16} />
            View All Calls
          </Button>
        </div>
      </div>

      {/* Dashboard Content */}
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <SpinnerLoader />
        </div>
      ) : error ? (
        <div className="p-4">
          <ErrorCard
            title="Unable to load evaluation data"
            message={error}
            onRetry={fetchData}
            isRetrying={loading}
            showBackButton={false}
          />
        </div>
      ) : (
        <>
          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4 mb-6">
            <StatCard
              title="Total Calls"
              value={stats?.totalCalls || 0}
              icon={<Phone size={24} />}
              variant="default"
            />
            <StatCard
              title="Human Requested"
              value={stats?.humanRequested || 0}
              icon={<User size={24} />}
              variant={(stats?.humanRequested || 0) > 0 ? 'danger' : 'success'}
            />
            <StatCard
              title="Routing Failed"
              value={stats?.routingFailed || 0}
              icon={<Route size={24} />}
              variant={(stats?.routingFailed || 0) > 0 ? 'danger' : 'success'}
            />
            <StatCard
              title="Frustrated Users"
              value={stats?.frustrated || 0}
              icon={<AlertTriangle size={24} />}
              variant={(stats?.frustrated || 0) > 0 ? 'warning' : 'success'}
            />
            <StatCard
              title="Questions Missed"
              value={stats?.questionsMissed || 0}
              icon={<HelpCircle size={24} />}
              variant={(stats?.questionsMissed || 0) > 0 ? 'warning' : 'success'}
            />
            <StatCard
              title="Name Not Captured"
              value={stats?.nameMissed || 0}
              icon={<User size={24} />}
              variant={(stats?.nameMissed || 0) > 0 ? 'warning' : 'success'}
            />
            <StatCard
              title="Phone Not Captured"
              value={stats?.phoneMissed || 0}
              icon={<Phone size={24} />}
              variant={(stats?.phoneMissed || 0) > 0 ? 'warning' : 'success'}
            />
          </div>

          {/* Flagged Calls */}
          <Card className="shadow-sm border border-gray-200 overflow-hidden">
            <CardHeader className="bg-white border-b border-gray-200 p-3">
              <CardTitle className="text-xl font-semibold flex items-center gap-2">
                <AlertTriangle className="text-yellow-500" size={20} />
                Flagged Calls ({flaggedTotal})
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {flaggedCalls.length === 0 ? (
                <div className="flex justify-center items-center h-40">
                  <div className="text-center">
                    <div className="text-4xl mb-2">✅</div>
                    <p className="text-gray-500">No flagged calls for this day</p>
                  </div>
                </div>
              ) : (
                <div className="max-h-[500px] overflow-y-auto">
                  {flaggedCalls.map(evaluation => (
                    <FlaggedCallCard
                      key={evaluation.id}
                      evaluation={evaluation}
                      onViewDetails={() => handleViewDetails(evaluation.meetingAssessmentId)}
                    />
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
